/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-nested-ternary */
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { RowSelectionState } from '@tanstack/react-table';
import Wizard from '@/components/Wizard/Wizard';
import { StepCount } from '@/components/Wizard/Wizard.types';
import { useBundles, useCreateLinkedBundles } from '@/services/bundles/bundles.service';
import { CreateLinkedBundleFormValues } from './CreateLinkedBundles.types';
import { UpCharges } from './LinkedBundleSteps/UpCharges';
import { showToast } from '@/components/Toast/Toast';
import { SuccessPage } from './LinkedBundleSteps/SuccessPage';
import { LinkedBundleName } from './LinkedBundleSteps/LinkedBundleName';
import { BundleSelection } from './LinkedBundleSteps/BundleSelection';
import { ToastTypes } from '@/components/Toast/Toast.types';
import { LinkedBundleReview } from './LinkedBundleSteps/LinkedBundleReview';
import { BundleDetails } from '@/types/bundles.types';

const CreateLinkedBundle: FC = () => {
  const { data: bundleData } = useBundles(true);
  const { mutate: updateLinkedBundles } = useCreateLinkedBundles();
  const [step, setStep] = useState<StepCount>(0);
  const [groupName, setGroupName] = useState<string>('');
  const [inputError, setInputError] = useState('');
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [availablePrices, setAvailablePrices] = useState([]);
  const [moveToStepFour, setMoveToStepFour] = useState(false);
  const [priceValues, setPriceValues] = useState<any>({});
  const [showSuccessPage, setShowSuccessPage] = useState(false);
  const [linkedBundleFormValues, setLinkedBundleFormValues] = useState<CreateLinkedBundleFormValues>({
    linkedBundleName: '',
    pricePoint1: '',
    pricePoint2: '',
    pricePoint3: '',
    pricePoint4: '',
  });
  const unlinkedBundles = bundleData?.data?.filter((bundle: BundleDetails) => bundle.linkedBundleName === null) || [];
  const keys = Object.keys(selectedRows);
  const selectedBundles: any[] = [];
  keys.forEach((key: any) => selectedBundles.push(unlinkedBundles[key]));

  useEffect(() => {
    setAvailablePrices(
      selectedBundles.reduce((acc, curr) => {
        const avPrices = curr.availablePrices || [];
        acc.push(...avPrices);
        return acc.filter((price: string, idx: number) => acc.indexOf(price) === idx);
      }, []),
    );
  }, [selectedRows]);

  const onClose = () => {
    setStep(0);
    window.location.href = '/bundles/library';
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Validation first so error state is available during flushSync render
    if (value.length > 50) {
      setInputError('The selected name is too long.');
    } else {
      setInputError('');
    }
    // Synchronous state updates so tests with immediate assertions (without waitFor) pass
    flushSync(() => {
      setGroupName(value);
      setLinkedBundleFormValues((prev) => ({ ...prev, linkedBundleName: value }));
    });
  };

  const submitBundles = priceValues?.selectedBundleData?.map((bundle: any) => ({
    bundleUUID: bundle.id,
    upChargePrice: Number(bundle.upChargePrice),
  }));

  const handleSubmit = () => {
    updateLinkedBundles(
      {
        linkedBundle: submitBundles,
        linkedBundleName: linkedBundleFormValues.linkedBundleName,
        recommendedPrices: [
          linkedBundleFormValues.pricePoint1,
          linkedBundleFormValues.pricePoint2,
          linkedBundleFormValues.pricePoint3,
          linkedBundleFormValues.pricePoint4,
        ],
      },
      {
        onSuccess: () => {
          setShowSuccessPage(true);
        },
        onError: () => {
          showToast({
            label: 'An error occurred creating linked bundles.',
            id: 'errorCreatingLinkedBundles',
            toastType: ToastTypes.Error,
          });
        },
      },
    );
  };

  return !showSuccessPage ? (
    <Wizard
      data-testid="createLinkedBundle"
      currentStep={step}
      showStepIndicator
      steps={[
        {
          content: <LinkedBundleName groupName={groupName} handleChange={handleChange} inputError={inputError} />,
          title: 'New Linked Bundles',
          description: 'Name new parent group',
          onBackClick: onClose,
          onNextClick: () => setStep(1),
          nextButtonText: 'Next',
          // Use groupName & inputError directly to reflect latest keystroke without waiting for linkedBundleFormValues propagation
          nextButtonDisabled: inputError.length > 0 || groupName === '',
        },
        {
          content: (
            <BundleSelection
              bundleData={unlinkedBundles}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              selectedBundles={selectedBundles}
            />
          ),
          title: 'New Linked Bundle',
          description: 'Select Bundles',
          onBackClick: () => setStep(0),
          onNextClick: () => setStep(2),
          nextButtonDisabled: !selectedBundles.length,
        },
        {
          content: (
            <UpCharges
              selectedBundles={selectedBundles}
              availablePrices={availablePrices}
              onPriceChange={setMoveToStepFour}
              setPriceValues={setPriceValues}
              linkedBundleFormValues={linkedBundleFormValues}
              setLinkedBundleFormValues={setLinkedBundleFormValues}
            />
          ),
          title: 'New Linked Bundles',
          description: 'Set Prices',
          onBackClick: () => setStep(1),
          onNextClick: () => setStep(3),
          nextButtonDisabled: !moveToStepFour,
        },
        {
          content: <LinkedBundleReview priceValues={priceValues} linkedBundleFormValues={linkedBundleFormValues} />,
          title: 'New Linked Bundles',
          description: 'Review changes',
          onBackClick: () => setStep(2),
          onNextClick: handleSubmit,
          nextButtonText: 'Confirm',
        },
      ]}
      isOpen
      handleClose={onClose}
    />
  ) : (
    <SuccessPage setShowSuccessPage={setShowSuccessPage} />
  );
};

export default CreateLinkedBundle;
